/**
 * GROUPS — access given to a team rather than to a person at a time.
 *
 * THE PROBLEM IT ANSWERS. Twenty boards and thirty people is six hundred
 * decisions, and they drift the moment anybody joins or leaves. Naming a group
 * on a board is one decision, and adding somebody to the group is another —
 * two, instead of twenty, and neither of them goes stale.
 *
 * WHERE MEMBERSHIP IS KEPT, AND WHY IT IS NOT ITS OWN COLLECTION.
 *
 *   groups/{id}          = { id, name }      the group, and what it is called
 *   roles/{email}.groups = [id, …]           which groups this person is in
 *   projects/{id}.groups = [id, …]           which groups may open this board
 *
 * The obvious shape is a member list on the group. It was rejected for one
 * reason: a permission rule may read at most ten documents, and a request for
 * one issue already reads the access list, the person's roles and the project.
 * Checking a member list on each group would be one more read per group — an
 * unbounded number, so a board named on four groups would simply stop working,
 * and it would stop working only for people in several groups. That is a fault
 * that appears in production and cannot be reproduced.
 *
 * Keeping it on `roles/{email}` costs NOTHING, because that document is already
 * being read to work out what the person may do. The check becomes: do any of
 * my groups appear on this board.
 *
 * THIS IS ALSO THE CLEAREST ARGUMENT FOR MOVING TO SQL. On Postgres this is a
 * join table and none of the above reasoning exists — no read budget, no shape
 * chosen to fit one, and "who is in the Design team" is a query rather than a
 * scan of every person. Written down here rather than in a commit message,
 * because it is the concrete version of a decision that is otherwise abstract.
 */
import type { Project } from './projects';

/**
 * ── TEAMS ARE HIDDEN, AND THIS IS THE ONE SWITCH — #337 ─────────────────────
 *
 * A team does exactly one thing: it opens the boards it is named on. The
 * DATABASE has enforced that correctly since migration 0061, where
 * `boards_i_can_open()` joins `board_groups` to `group_people`.
 *
 * ⚠️ THE BROWSER NEVER WRITES THE MEMBERSHIP AND NEVER READS IT BACK.
 * `setInGroup` patches `roles/{email}` with `{ groups }`, which reaches
 * `writePerson(…, 'role')`, whose whole patch is `{ default_role: rec.role }` —
 * the field is dropped and `rec.role` is undefined. The read asks for `email,
 * default_role`, so `groupsOf` is permanently `{}`. Both halves are missing, so
 * they agree with each other and the screens look right.
 *
 * WHICH MADE IT WORSE THAN USELESS. An admin who made a team, put three people
 * in it and reserved a board for it was told all three worked and had all three
 * written into the trail. The board became admin-only and nothing said so.
 *
 * SO IT IS HIDDEN RATHER THAN FIXED — Vinnie, 2 September 2026: *"Why should we
 * fix it if it is not being used right now?"* Teams belong in the tenant admin
 * screen (#338), which does not exist, and its shape decides theirs. Fixing the
 * mapping first would be a feature with nowhere to reach it.
 *
 * ⛔ TURNING THIS ON DOES NOT MAKE TEAMS WORK. The screens come back; the
 * mapping is still missing. Fix `src/backend/paths.ts` first — see #337.
 */
export const TEAMS_ARE_CONNECTED: boolean = false;

export interface Group {
  id: string;
  name: string;
}

/** Which groups each person is in, by email. Sparse — most people are in none. */
export type GroupsOf = Record<string, string[]>;

/** An id from a name. Not shown anywhere; permanent once made. */
export function groupId(name: string, unique: string): string {
  const stem =
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 24) || 'group';
  return `${stem}-${unique}`;
}

/** What stops a group being made, in the words to show somebody. */
export function groupProblem(name: string, existing: Pick<Group, 'name'>[]): string {
  const n = name.trim();
  if (!n) return 'Give the group a name.';
  if (n.length > 40) return 'That name is too long — keep it under 40 characters.';
  if (existing.some((g) => g.name.trim().toLowerCase() === n.toLowerCase()))
    return `There is already a group called ${n}.`;
  return '';
}

/** The groups this person belongs to, tidied. */
export function groupsFor(groupsOf: GroupsOf, email: string): string[] {
  return (groupsOf[email.trim().toLowerCase()] ?? []).filter(Boolean);
}

/** Everybody in a group. Read from each person, since that is where it lives. */
export function membersOf(groupsOf: GroupsOf, groupId: string): string[] {
  return Object.entries(groupsOf)
    .filter(([, ids]) => ids.includes(groupId))
    .map(([email]) => email)
    .sort();
}

/**
 * WHY THIS PERSON CAN OPEN THIS BOARD — which is not the same question as
 * whether they can.
 *
 * A tick that does not say where it came from is the thing that made the old
 * screen confusing: somebody appears to be on a board, an admin unticks them,
 * and nothing happens, because their access was never the tick. Naming the
 * reason is what makes the control honest.
 */
export type AccessVia =
  | { via: 'admin' }
  | { via: 'everyone' }
  | { via: 'named' }
  | { via: 'group'; groupId: string }
  | { via: 'none' };

export function accessVia(
  project: Pick<Project, 'members' | 'groups' | 'sample'>,
  email: string,
  isAdmin: boolean,
  groupsOf: GroupsOf
): AccessVia {
  const named = (project.members ?? []).map((m) => m.trim().toLowerCase()).filter(Boolean);
  const onBoard = (project.groups ?? []).filter(Boolean);
  const me = email.trim().toLowerCase();

  /*
   * A READ-ONLY SAMPLE IS OPEN TO EVERYONE IN THE WORKSPACE — even though the
   * worked example names its cast as the board's people. The samples exist to
   * show a newcomer a well-run board, and a newcomer is usually not an admin, so
   * reserving them to the cast + admins hid them from the very people they are
   * for. Everyone may READ a sample; writes stay blocked by the sample read-only
   * guard. This mirrors boards_i_can_open() in the database (migration 0189),
   * which opens the sample's rows to every member while leaving the write gate
   * (can_open) untouched.
   */
  if (project.sample) return { via: 'everyone' };

  /*
   * OPEN COMES FIRST, and it means BOTH lists empty. A board named on a group
   * and on nobody is reserved for that group — reading the member list alone
   * would call it open to everyone, which is the failure that matters: a board
   * meant for one team, readable by the whole workspace, and nothing on screen
   * saying so.
   */
  if (named.length === 0 && onBoard.length === 0) return { via: 'everyone' };
  // Checked after "open", so an admin on an open board is not told they are
  // there by rank — they are there like everybody else.
  if (isAdmin) return { via: 'admin' };
  if (named.includes(me)) return { via: 'named' };
  const mine = groupsFor(groupsOf, me);
  const match = onBoard.find((g) => mine.includes(g));
  return match ? { via: 'group', groupId: match } : { via: 'none' };
}

export function canOpen(
  project: Pick<Project, 'members' | 'groups' | 'sample'>,
  email: string,
  isAdmin: boolean,
  groupsOf: GroupsOf
): boolean {
  return accessVia(project, email, isAdmin, groupsOf).via !== 'none';
}

/**
 * WHETHER TAKING SOMEBODY OFF A BOARD BY NAME WILL ACTUALLY DO ANYTHING.
 *
 * If their access comes from a group, removing them from the board's named
 * list removes nothing — they are not on it. The button would run, report
 * success, and leave them exactly where they were.
 *
 * So the screen has to know, and say: remove them from the group, or take the
 * group off the board. Both are real choices; a Remove that quietly achieves
 * neither is not.
 */
export function removalProblem(
  project: Pick<Project, 'members' | 'groups' | 'name'>,
  email: string,
  groupsOf: GroupsOf,
  groups: Group[]
): string {
  const how = accessVia(project, email, false, groupsOf);
  if (how.via !== 'group') return '';
  const name = groups.find((g) => g.id === how.groupId)?.name ?? 'a group';
  return `They can open ${project.name} through the ${name} group, not by name — so removing them here would change nothing. Take them out of ${name}, or take ${name} off this board.`;
}

/** What to say on a row, so a tick is never unexplained. */
export function accessReason(how: AccessVia, groups: Group[]): string {
  switch (how.via) {
    case 'admin':
      return 'Admin — every board';
    case 'everyone':
      return 'Open to everyone in the workspace';
    case 'named':
      return 'Named on this board';
    case 'group':
      return `Through ${groups.find((g) => g.id === how.groupId)?.name ?? 'a group'}`;
    default:
      return '';
  }
}
